/**
 * Lifts the agent's closing "Next steps" list out of the narrative.
 *
 * The agent ends most replies with a heading and a list of questions it could
 * answer next. As Markdown they are text the user has to retype; pulled out,
 * each one becomes a button that sends it.
 *
 * Only a list that closes the reply is taken. Anything written after it — other
 * than the metadata fences, which render nothing — means the list is part of
 * the argument, and moving it to the end would reorder what the agent said.
 */

const TOPIC =
  /next steps|exploration|explore further|keep exploring|follow[- ]?ups?|suggested questions|where to go next/i;

const HEADING = /^\s{0,3}(?:#{1,6}\s+(.+?)\s*#*\s*|\*\*(.+?)\*\*:?|__(.+?)__:?)\s*$/;
const ITEM = /^\s{0,3}(?:\d+[.)]|[-*+])\s+(.*)$/;
const CONTINUATION = /^\s{2,}\S/;

/** Fences that render nothing, so they may follow the list. */
const SILENT_FENCES = /```(?:response-metadata|json)\b[\s\S]*?(?:```|$)/g;

// Bold is kept: it marks the segment or brand the question is about, and the
// buttons show it. `stripMarks` gives the text to send.
const tidy = (text) => text.replace(/\s+/g, " ").trim();

export const stripMarks = (text) => text.replace(/\*\*|__/g, "");

export const extractNextSteps = (text) => {
  const none = { body: text, suggestions: [], heading: undefined };
  if (typeof text !== "string" || !text) return none;

  const lines = text.split("\n");
  let heading;
  const start = lines.findIndex((line) => {
    const m = HEADING.exec(line);
    const title = m && (m[1] || m[2] || m[3]);
    if (!title || !TOPIC.test(title)) return false;
    heading = title.trim();
    return true;
  });
  if (start === -1) return none;

  const items = [];
  let end = start + 1;
  for (; end < lines.length; end += 1) {
    const line = lines[end];
    if (!line.trim()) continue;
    const item = ITEM.exec(line);
    if (item) {
      items.push(item[1]);
    } else if (items.length && CONTINUATION.test(line)) {
      items[items.length - 1] += ` ${line.trim()}`;
    } else {
      break;
    }
  }

  const rest = lines.slice(end).join("\n");
  if (!items.length || rest.replace(SILENT_FENCES, "").trim()) return none;

  // A `---` the agent put above the list belonged to the list; the buttons
  // draw their own line, and leaving it would make two.
  const before = lines.slice(0, start).join("\n").replace(/\n\s*(?:-{3,}|\*{3,}|_{3,})\s*$/, "");

  return {
    body: [before, rest].join("\n").trimEnd(),
    suggestions: items.map(tidy).filter(Boolean),
    // The agent's own heading for the section.
    heading,
  };
};

/**
 * The follow-up questions to offer as chips above the message box - the only
 * place they are shown.
 *
 * The agent's `suggestions` win. A reply that came without them - every reply
 * in a conversation opened from history - falls back to its own closing
 * next-steps list, so the latest answer still offers its follow-ups.
 */
export const followUpsFor = (suggestions, replyText) => {
  if (Array.isArray(suggestions) && suggestions.length) return suggestions;
  return extractNextSteps(replyText).suggestions.map(stripMarks);
};

export default extractNextSteps;
